import React, { useEffect, useRef, useState } from 'react';
import DailyIframe from '@daily-co/daily-js';
import { DailyProvider, useDaily, useLocalParticipant, useParticipantIds,
         useMediaTrack, useScreenShare } from '@daily-co/daily-react';
import { Mic, MicOff, Video, VideoOff, MonitorUp, MonitorX, PhoneOff, Sparkles, Loader2, AlertCircle } from 'lucide-react';

/**
 * Winhouse Classroom — Daily-powered video panel.
 *
 * White-label: no Daily logos / no DailyPrebuilt. We use the headless call
 * object + daily-react hooks to render our OWN tiles + controls.
 */

// Translate provider-specific errors into Winhouse-branded copy. Returns
// null when the message is already safe to show to end users.
function translateVideoError(raw) {
  if (!raw) return null;
  const s = String(raw);
  if (s.includes('Permission denied') || s.includes('NotAllowedError')) {
    return 'Browser blocked camera or microphone access. Please allow access and refresh.';
  }
  if (s.includes('NotFoundError') || s.includes('no camera') || s.includes('no microphone')) {
    return 'No camera or microphone detected on this device.';
  }
  if (s.includes('account-missing-payment-method')) {
    return 'Video service is not yet activated. Admin: add a payment method to the Winhouse video account, then re-create the booking.';
  }
  if (s.includes('Duplicate') || s.includes('DailyIframe')) {
    return 'Reconnecting to the classroom — please refresh if this persists.';
  }
  if (s.includes('Failed to load call object bundle') || s.includes('call-machine')) {
    return 'Could not reach the video service. Please check your internet connection and try again.';
  }
  if (s.includes('not-before') || s.includes('nbf')) {
    return 'This class has not started yet. Please come back at the scheduled time.';
  }
  if (s.includes('expired') || s.includes('exp')) {
    return 'This class has ended.';
  }
  if (s.includes('unauthorized') || s.includes('auth')) {
    return 'Your access link is invalid or has expired.';
  }
  // Catch-all: never let raw provider URLs or SDK strings leak through.
  if (/daily\.co|daily-co|dailyiframe|dailyjs/i.test(s)) {
    return 'The video service is temporarily unavailable. Please try again in a moment.';
  }
  return null;
}

// ---- helpers ---------------------------------------------------------------

const VideoTile = ({ sessionId, isLocal = false, fallbackName }) => {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const videoTrack = useMediaTrack(sessionId, 'video');
  const audioTrack = useMediaTrack(sessionId, 'audio');

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (videoTrack?.persistentTrack) {
      el.srcObject = new MediaStream([videoTrack.persistentTrack]);
      el.play?.().catch(() => {});
    } else {
      el.srcObject = null;
    }
  }, [videoTrack?.persistentTrack]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || isLocal) return;
    if (audioTrack?.persistentTrack) {
      el.srcObject = new MediaStream([audioTrack.persistentTrack]);
      el.play?.().catch(() => {});
    } else {
      el.srcObject = null;
    }
  }, [audioTrack?.persistentTrack, isLocal]);

  const off = !videoTrack?.persistentTrack || videoTrack?.state === 'off';

  return (
    <div className="relative w-full h-full rounded-xl overflow-hidden bg-gradient-to-br from-[#1a0e0e] to-[#0a0a0a] border border-white/10 transition-opacity duration-500">
      <video ref={videoRef} autoPlay playsInline muted={isLocal}
             className={`w-full h-full object-cover transition-opacity duration-300 ${off ? 'opacity-0' : 'opacity-100'}`} />
      {!isLocal && <audio ref={audioRef} autoPlay />}
      {off && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
          <div className="w-16 h-16 rounded-full bg-white/10 border border-white/20 flex items-center justify-center mb-2">
            <span className="text-xl font-bold">{(fallbackName || '?')[0]?.toUpperCase()}</span>
          </div>
          <div className="text-sm text-white/80">{fallbackName}</div>
          <div className="text-[10px] uppercase tracking-wider text-white/40 mt-1">Camera off</div>
        </div>
      )}
      <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-black/70 text-white text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
        {isLocal ? 'You' : fallbackName}
      </div>
    </div>
  );
};

const ScreenTile = ({ sessionId }) => {
  const videoRef = useRef(null);
  const track = useMediaTrack(sessionId, 'screenVideo');
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (track?.persistentTrack) {
      el.srcObject = new MediaStream([track.persistentTrack]);
      el.play?.().catch(() => {});
    } else { el.srcObject = null; }
  }, [track?.persistentTrack]);
  return (
    <div className="w-full h-full rounded-xl overflow-hidden bg-black border border-white/10">
      <video ref={videoRef} autoPlay playsInline className="w-full h-full object-contain" />
      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-[#D3D86F] text-black text-[10px] uppercase tracking-wider font-bold">Screen share</div>
    </div>
  );
};

// ---- inner UI --------------------------------------------------------------

const DailyClassroomInner = ({ role, yourName, counterpartName, onLeave }) => {
  const call = useDaily();
  const local = useLocalParticipant();
  const remoteIds = useParticipantIds({ filter: 'remote' });
  const { screens, startScreenShare, stopScreenShare, isSharingScreen } = useScreenShare();
  const [networkState, setNetworkState] = useState('connected');

  // Track network reconnect lifecycle
  useEffect(() => {
    if (!call) return;
    const onQuality = (e) => {
      if (e?.threshold === 'very-low' || e?.quality < 25) setNetworkState('weak');
      else setNetworkState('connected');
    };
    const onReconnecting = () => setNetworkState('reconnecting');
    const onReconnected = () => setNetworkState('connected');
    call.on('network-quality-change', onQuality);
    call.on('network-connection', (e) => {
      if (e?.type === 'signaling' && e?.event === 'interrupted') setNetworkState('reconnecting');
      if (e?.type === 'signaling' && e?.event === 'connected') setNetworkState('connected');
    });
    call.on('reconnecting', onReconnecting);
    call.on('reconnected', onReconnected);
    return () => {
      try {
        call.off('network-quality-change', onQuality);
        call.off('reconnecting', onReconnecting);
        call.off('reconnected', onReconnected);
      } catch (_e) {}
    };
  }, [call]);

  const audioOn = local?.tracks?.audio?.state === 'playable';
  const videoOn = local?.tracks?.video?.state === 'playable';
  const camBlocked = local?.tracks?.video?.blocked?.byPermissions;
  const micBlocked = local?.tracks?.audio?.blocked?.byPermissions;
  const remoteConnected = remoteIds.length > 0;

  const toggleAudio = () => call?.setLocalAudio(!audioOn);
  const toggleVideo = () => call?.setLocalVideo(!videoOn);
  const toggleShare = () => isSharingScreen ? stopScreenShare() : startScreenShare();
  const leave = async () => {
    try { await call?.leave(); } catch (_e) {}
    try { await call?.destroy(); } catch (_e) {}
    // Clear module singleton so next visit creates a fresh instance.
    MODULE_CALL_OBJECT = null;
    MODULE_CALL_KEY = null;
    onLeave?.();
  };

  return (
    <div className="relative w-full h-full min-h-[460px] rounded-2xl overflow-hidden bg-[#0a0a0a] border border-white/10"
         data-testid="classroom-video-panel">
      {/* Brand watermark */}
      <div className="absolute top-3 left-3 z-20 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 border border-white/10 backdrop-blur">
        <Sparkles className="h-3.5 w-3.5 text-[#D3D86F]" />
        <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-white">Winhouse Classroom</span>
      </div>
      <div className="absolute top-3 right-3 z-20 flex items-center gap-2">
        {networkState === 'reconnecting' && (
          <span className="px-3 py-1.5 rounded-full bg-amber-500 text-black text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 animate-pulse"
                data-testid="classroom-reconnecting">
            <Loader2 className="h-3 w-3 animate-spin" /> Reconnecting…
          </span>
        )}
        {networkState === 'weak' && (
          <span className="px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 text-[10px] font-bold uppercase tracking-wider">
            Weak signal
          </span>
        )}
        <span className="px-3 py-1.5 rounded-full bg-[#D3D86F] text-black text-[11px] font-bold uppercase tracking-wider">
          {role}
        </span>
      </div>

      {(camBlocked || micBlocked) && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-30 max-w-md px-4 py-3 rounded-xl bg-red-600 text-white text-xs shadow-lg flex items-center gap-2 animate-fade-in"
             data-testid="classroom-permission-banner">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>
            {camBlocked && micBlocked && 'Camera and microphone are blocked. '}
            {camBlocked && !micBlocked && 'Camera is blocked. '}
            {!camBlocked && micBlocked && 'Microphone is blocked. '}
            Click the lock icon in your browser's address bar to allow access, then refresh.
          </span>
        </div>
      )}

      {/* Tiles */}
      <div className="absolute inset-0 p-4 pt-14 pb-20">
        {screens.length > 0 ? (
          <div className="w-full h-full grid grid-rows-[1fr_120px] gap-2">
            <div className="relative">
              <ScreenTile sessionId={screens[0].session_id} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {local && <VideoTile sessionId={local.session_id} isLocal fallbackName={yourName} />}
              {remoteIds.slice(0, 1).map((id) => (
                <VideoTile key={id} sessionId={id} fallbackName={counterpartName} />
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full h-full">
            {local && <VideoTile sessionId={local.session_id} isLocal fallbackName={yourName} />}
            {remoteIds.length > 0 ? (
              remoteIds.map((id) => (
                <VideoTile key={id} sessionId={id} fallbackName={counterpartName} />
              ))
            ) : (
              <div className="rounded-xl border border-dashed border-white/15 flex flex-col items-center justify-center text-white/60 text-sm bg-white/[0.02]"
                   data-testid="classroom-waiting-tile">
                <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-2 animate-pulse">
                  <Video className="h-5 w-5" />
                </div>
                <div className="font-semibold text-white/80">Waiting for {counterpartName}</div>
                <div className="text-[10px] uppercase tracking-wider text-white/40 mt-1">They'll appear here when they join</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom controls */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        <button onClick={toggleAudio}
          className={`h-11 px-4 rounded-full flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider border ${audioOn ? 'bg-white/10 text-white border-white/15 hover:bg-white/20' : 'bg-red-600 text-white border-red-700'}`}
          data-testid="classroom-mic-btn">
          {audioOn ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
          {audioOn ? 'Mic' : 'Muted'}
        </button>
        <button onClick={toggleVideo}
          className={`h-11 px-4 rounded-full flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider border ${videoOn ? 'bg-white/10 text-white border-white/15 hover:bg-white/20' : 'bg-red-600 text-white border-red-700'}`}
          data-testid="classroom-cam-btn">
          {videoOn ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
          {videoOn ? 'Camera' : 'Off'}
        </button>
        <button onClick={toggleShare}
          className={`h-11 px-4 rounded-full flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider border ${isSharingScreen ? 'bg-[#D3D86F] text-black border-[#D3D86F]' : 'bg-white/10 text-white border-white/15 hover:bg-white/20'}`}
          data-testid="classroom-share-btn">
          {isSharingScreen ? <MonitorX className="h-4 w-4" /> : <MonitorUp className="h-4 w-4" />}
          {isSharingScreen ? 'Stop share' : 'Share'}
        </button>
        <button onClick={leave}
          className="h-11 px-4 rounded-full flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider bg-red-600 hover:bg-red-700 text-white"
          data-testid="classroom-hangup-btn">
          <PhoneOff className="h-4 w-4" /> Hang up
        </button>
      </div>
    </div>
  );
};

// ---- outer shell -----------------------------------------------------------

// Module-level singleton (survives React StrictMode double-mounts in dev).
let MODULE_CALL_OBJECT = null;
let MODULE_CALL_KEY = null;

// Expose the active Daily call object so sibling components (multi-board sync,
// document panel, hand-raise) can hook into the same WebRTC data channel.
export function getActiveCallObject() {
  return MODULE_CALL_OBJECT;
}

const DailyVideoPanel = ({ roomUrl, token, role, yourName, counterpartName, onLeave }) => {
  const [callObject, setCallObject] = useState(null);
  const [error, setError] = useState(null);
  const [joined, setJoined] = useState(false);

  const cacheKey = `${roomUrl}::${token}`;

  useEffect(() => {
    let cancelled = false;

    // Reuse existing call object if it's for the same (url, token).
    if (MODULE_CALL_OBJECT && MODULE_CALL_KEY === cacheKey) {
      setCallObject(MODULE_CALL_OBJECT);
      const state = MODULE_CALL_OBJECT.meetingState?.();
      if (state === 'joined-meeting') setJoined(true);
      else {
        // Already created but not joined yet — wait for join.
        MODULE_CALL_OBJECT.join({ url: roomUrl, token })
          .then(() => { if (!cancelled) setJoined(true); })
          .catch((e) => {
            if (cancelled) return;
            const m = e?.errorMsg || e?.message || 'Could not join';
            setError(translateVideoError(String(m)) || m);
          });
      }
      return () => { cancelled = true; };
    }

    // Different (url, token) or first ever — tear down any previous instance.
    if (MODULE_CALL_OBJECT) {
      try { MODULE_CALL_OBJECT.destroy(); } catch (_e) {}
      MODULE_CALL_OBJECT = null;
      MODULE_CALL_KEY = null;
    }
    try {
      const existing = DailyIframe.getCallInstance?.();
      if (existing) existing.destroy();
    } catch (_e) {}

    let co;
    try {
      // First-attempt creation
      co = DailyIframe.createCallObject({ url: roomUrl, token });
    } catch (e) {
      // Recover from "Duplicate DailyIframe instances are not allowed":
      // reuse the existing global instance instead of failing.
      const msg = String(e?.message || '');
      const existing = DailyIframe.getCallInstance?.();
      if (existing && (msg.includes('Duplicate') || msg.includes('DailyIframe'))) {
        co = existing;
      } else {
        setError(translateVideoError(msg) || 'Could not initialize video');
        return;
      }
    }
    MODULE_CALL_OBJECT = co;
    MODULE_CALL_KEY = cacheKey;
    setCallObject(co);
    let lastDailyErrorMsg = null;
    co.on('error', (e) => {
      if (cancelled) return;
      // eslint-disable-next-line no-console
      console.warn('[Winhouse Classroom] video error event:', e);
      const m = e?.errorMsg || e?.error || e?.message || 'Meeting error';
      lastDailyErrorMsg = String(m);
      const translated = translateVideoError(lastDailyErrorMsg);
      if (translated) setError(translated);
    });
    co.join({ url: roomUrl, token })
      .then(() => { if (!cancelled) setJoined(true); })
      .catch((e) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn('[Winhouse Classroom] join rejected:', e);
        // Prefer the last Daily error event message (richer), then the catch value
        const raw = lastDailyErrorMsg || e?.errorMsg || e?.error || e?.message
                  || (typeof e === 'string' ? e : '') || 'Could not join the classroom';
        setError(translateVideoError(String(raw)) || raw);
      });

    return () => {
      cancelled = true;
      // Don't destroy on unmount — let the next mount reuse if same key.
      // Cleanup happens when (url,token) changes OR when user clicks Leave.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  if (error) {
    return (
      <div className="w-full min-h-[460px] rounded-2xl bg-[#0a0a0a] border border-white/10 flex flex-col items-center justify-center text-white p-8 text-center"
           data-testid="classroom-video-error">
        <AlertCircle className="h-7 w-7 text-red-400 mb-3" />
        <div className="text-base font-bold mb-1">Video could not start</div>
        <div className="text-xs text-white/70 max-w-sm">{error}</div>
      </div>
    );
  }

  if (!callObject || !joined) {
    return (
      <div className="w-full min-h-[460px] rounded-2xl bg-[#0a0a0a] border border-white/10 flex flex-col items-center justify-center text-white"
           data-testid="classroom-video-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#D3D86F] mb-3" />
        <div className="text-[11px] uppercase tracking-[0.2em] text-white/60">Winhouse Classroom</div>
        <div className="text-sm text-white/80 mt-1">Connecting you to the room…</div>
      </div>
    );
  }

  return (
    <DailyProvider callObject={callObject}>
      <DailyClassroomInner role={role} yourName={yourName} counterpartName={counterpartName} onLeave={onLeave} />
    </DailyProvider>
  );
};

export default DailyVideoPanel;
